import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/content";

const NAV = [
  { label: "So funktioniert es", href: "#so-funktioniert-es" },
  { label: "Vorteile", href: "#vorteile" },
  { label: "Ideal für", href: "#ideal-fuer" },
  { label: "Bewertungen", href: "#bewertungen" },
  { label: "Fragen", href: "#faq" },
  { label: "Kontakt", href: "#kontakt" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkPrefix = onLanding ? "" : "/";

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur transition-shadow ${scrolled ? "shadow-[0_6px_20px_rgba(15,42,71,0.08)] border-b border-border" : "border-b border-transparent"}`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo" aria-label="Startseite">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[color:var(--brand-navy)] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 11.5 12 5l8 6.5V20a1 1 0 0 1-1 1h-4v-5h-6v5H5a1 1 0 0 1-1-1v-8.5Z" stroke="#7AB648" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
          </span>
          <span className="leading-tight">
            <span className="block font-display text-2xl text-[color:var(--brand-navy)]">HAUSNOTRUF</span>
            <span className="block text-[11px] font-semibold tracking-wide text-[color:var(--brand-green-alt)] -mt-1">SICHER ZUHAUSE</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Hauptnavigation">
          {NAV.map((n) => (
            <a key={n.href} href={`${linkPrefix}${n.href}`} className="whitespace-nowrap text-sm font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-green-alt)] transition-colors">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden sm:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneHref}`}
            data-testid="header-hotline-link"
            className="hidden xl:flex items-center gap-2 whitespace-nowrap text-[color:var(--brand-navy)] font-bold text-base hover:text-[color:var(--brand-green-alt)] transition-colors"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.phoneDisplay}
          </a>
          <Button asChild className="h-11 px-5 rounded-xl bg-[color:var(--brand-green)] hover:bg-[color:var(--brand-green-alt)] text-white font-semibold">
            <a href={`${linkPrefix}#kontakt`} data-testid="header-callback-button">Rückruf anfordern</a>
          </Button>
        </div>

        <button
          className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-xl border border-border text-[color:var(--brand-navy)]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
          aria-expanded={open}
          data-testid="header-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-menu">
          <nav className="container-x py-4 flex flex-col gap-1" aria-label="Mobile Navigation">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={`${linkPrefix}${n.href}`}
                onClick={() => setOpen(false)}
                className="py-3 px-2 rounded-lg text-base font-semibold text-[color:var(--brand-navy)] hover:bg-secondary"
              >
                {n.label}
              </a>
            ))}
            <a href={`tel:${COMPANY.phoneHref}`} className="mt-2 py-3 px-2 rounded-lg text-base font-bold text-[color:var(--brand-green-alt)] flex items-center gap-2">
              <Phone className="h-5 w-5" /> {COMPANY.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
